import { z } from "zod";

/**
 * Yêu cầu hỗ trợ gửi từ app (US-AE11.F4-1026 · @UC9105 admin · @UC9106 app). Một nguồn sự thật cho api (hai làn: app gửi/xem
 * "yêu cầu của tôi", admin xử lý) + admin web + mobile.
 *
 * Mô hình: TÁI DÙNG `support_tickets` (`source = in_app`) + `support_messages` (nội dung = message `inbound` đầu tiên) — không dựng
 * bảng ticket thứ hai. Hộp thư hỗ trợ cũ (@UC9103) giữ nguyên.
 *
 * BẤT BIẾN:
 * - G2: 0 bút toán — chỉ hội thoại hỗ trợ, không field tiền.
 * - Ghi chú xử lý của admin là NỘI BỘ: KHÔNG có trong DTO của làn app (QĐ người dùng 23/10/2026).
 * - Số điện thoại người gửi che theo vai admin như trang Người dùng (`redactPhone` — QĐ 23/10).
 * - Email báo admin mang mã + loại + link + nội dung + tên người gửi, KHÔNG SĐT / ảnh (QĐ 26/10, đảo phần email của QĐ 23/10).
 * - `.strict()` cho mọi body ghi — field lạ ⇒ 400 ở biên.
 */

// ============================================================
// Hằng + enum
// ============================================================

/** Loại góp ý (màn "Gửi góp ý"): Đề xuất tính năng · Báo lỗi · Khác. Khớp CHECK `support_tickets_category_check`. */
export const SUPPORT_REQUEST_CATEGORIES = ["feature_request", "bug", "other"] as const;
export const supportRequestCategorySchema = z.enum(SUPPORT_REQUEST_CATEGORIES);
export type SupportRequestCategory = z.infer<typeof supportRequestCategorySchema>;

/** Nền tảng thiết bị gửi. Khớp CHECK trong migration. */
export const SUPPORT_REQUEST_PLATFORMS = ["ios", "android", "web"] as const;
export const supportRequestPlatformSchema = z.enum(SUPPORT_REQUEST_PLATFORMS);

export const SUPPORT_REQUEST_BODY_MAX_LENGTH = 2000;
export const SUPPORT_REQUEST_DEVICE_FIELD_MAX_LENGTH = 100;
export const SUPPORT_REQUEST_RESOLUTION_NOTE_MAX_LENGTH = 2000;
/** Số ảnh tối đa một yêu cầu. Mỗi ảnh ≤ `PROOF_MAX_BYTES` (dùng chung giới hạn ảnh bằng chứng). */
export const SUPPORT_REQUEST_MAX_FILES = 3;
/** Tên field multipart chứa ảnh (lặp lại tối đa `SUPPORT_REQUEST_MAX_FILES` lần). */
export const SUPPORT_REQUEST_FILES_FIELD = "files";
/** Prefix vùng chứa R2 — tách khỏi `proofs/` và `dispute-evidence/`. */
export const SUPPORT_REQUEST_KEY_PREFIX = "support-requests";
export const SUPPORT_REQUEST_LIST_MAX_LIMIT = 100;

/** Mã lỗi ổn định (client map code → câu). */
export const SUPPORT_REQUEST_GROUP_NOT_MEMBER_CODE = "SUPPORT_REQUEST_GROUP_NOT_MEMBER";
export const SUPPORT_REQUEST_TOO_MANY_FILES_CODE = "SUPPORT_REQUEST_TOO_MANY_FILES";
export const SUPPORT_REQUEST_NOT_RESOLVABLE_CODE = "SUPPORT_REQUEST_NOT_RESOLVABLE";
export const SUPPORT_REQUEST_NOT_REOPENABLE_CODE = "SUPPORT_REQUEST_NOT_REOPENABLE";

// ============================================================
// Làn APP — POST /v1/support/requests (multipart) · GET /v1/support/requests
// ============================================================

const deviceField = z.string().trim().min(1).max(SUPPORT_REQUEST_DEVICE_FIELD_MAX_LENGTH);

/**
 * Các field chữ của form multipart (ảnh đi riêng ở field `files`). Thông tin thiết bị KHÔNG bắt buộc (QĐ 23/10) — server gom lại
 * thành cột `deviceInfo`. `groupId` tuỳ chọn: phải là nhóm người gửi đang tham gia, nếu không ⇒ 422.
 */
export const createSupportRequestSchema = z
  .object({
    category: supportRequestCategorySchema,
    body: z.string().trim().min(1).max(SUPPORT_REQUEST_BODY_MAX_LENGTH),
    groupId: z.string().uuid().optional(),
    platform: supportRequestPlatformSchema.optional(),
    appVersion: deviceField.optional(),
    osVersion: deviceField.optional(),
    deviceModel: deviceField.optional(),
  })
  .strict();
export type CreateSupportRequestBody = z.infer<typeof createSupportRequestSchema>;

/** Thông tin thiết bị lúc gửi (mọi field có thể vắng). */
export const supportRequestDeviceInfoSchema = z
  .object({
    platform: supportRequestPlatformSchema.nullable(),
    appVersion: z.string().nullable(),
    osVersion: z.string().nullable(),
    deviceModel: z.string().nullable(),
  })
  .strict();
export type SupportRequestDeviceInfo = z.infer<typeof supportRequestDeviceInfoSchema>;

/**
 * Trạng thái người gửi thấy: chỉ hai — `open` (Chưa xử lý, gồm cả `pending` = đã mở lại) và `resolved` (Đã xử lý, gồm `closed`).
 */
export const mySupportRequestStatusSchema = z.enum(["open", "resolved"]);
export type MySupportRequestStatus = z.infer<typeof mySupportRequestStatusSchema>;

/**
 * Ảnh đính kèm — CHUNG cho làn app và làn admin (một lõi, khác controller). `url` = URL đọc ngắn hạn ký LÚC ĐỌC (không lưu URL; hết hạn ⇒
 * gọi lại API để lấy URL mới); null khi kho ảnh không ký được (hiện "không tải được", không vỡ màn).
 */
export const supportRequestAttachmentSchema = z
  .object({
    id: z.string(),
    url: z.string().nullable(),
    expiresAt: z.string().nullable(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
  })
  .strict();
export type SupportRequestAttachment = z.infer<typeof supportRequestAttachmentSchema>;

/** Một yêu cầu của chính người gửi. KHÔNG có ghi chú xử lý / người xử lý (nội bộ). */
export const mySupportRequestSchema = z
  .object({
    id: z.string(),
    /** Mã hiển thị `#YC-<number>`. */
    number: z.number().int().positive(),
    category: supportRequestCategorySchema,
    status: mySupportRequestStatusSchema,
    body: z.string(),
    groupId: z.string().nullable(),
    /** Ảnh người gửi đính kèm — cùng hình với làn admin, để app hiện ảnh lớn ngay trong màn. */
    attachments: z.array(supportRequestAttachmentSchema),
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
  })
  .strict();
export type MySupportRequest = z.infer<typeof mySupportRequestSchema>;

export const mySupportRequestListQuerySchema = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(SUPPORT_REQUEST_LIST_MAX_LIMIT).optional(),
  })
  .strict();
export type MySupportRequestListQuery = z.infer<typeof mySupportRequestListQuerySchema>;

const pageInfoSchema = z
  .object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  })
  .strict();

export const mySupportRequestListResponseSchema = z
  .object({
    data: z.array(mySupportRequestSchema),
    pageInfo: pageInfoSchema,
  })
  .strict();
export type MySupportRequestListResponse = z.infer<typeof mySupportRequestListResponseSchema>;

// ============================================================
// Làn ADMIN — /v1/admin/support-requests
// ============================================================

/** Tab: Chưa xử lý (open + pending) · Đã xử lý (resolved + closed) · Tất cả. */
export const adminSupportRequestBucketSchema = z.enum(["unresolved", "resolved", "all"]);
export type AdminSupportRequestBucket = z.infer<typeof adminSupportRequestBucketSchema>;

export const adminSupportRequestListQuerySchema = z
  .object({
    bucket: adminSupportRequestBucketSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(SUPPORT_REQUEST_LIST_MAX_LIMIT).optional(),
  })
  .strict();
export type AdminSupportRequestListQuery = z.infer<typeof adminSupportRequestListQuerySchema>;

/** Trạng thái ticket thật (vòng đời chung với hộp thư cũ). */
export const adminSupportRequestStatusSchema = z.enum(["open", "pending", "resolved", "closed"]);

/**
 * Vai của người gửi trong nhóm đã chọn: vai ghế chính chủ, hoặc `representative` khi chỉ đại diện suất qua liên kết (phụ huynh).
 * null = không chọn nhóm / không còn trong nhóm.
 */
export const supportRequesterRoleSchema = z.enum(["owner", "admin", "treasurer", "member", "representative"]);
export type SupportRequesterRole = z.infer<typeof supportRequesterRoleSchema>;

export const adminSupportRequestItemSchema = z
  .object({
    id: z.string(),
    number: z.number().int().positive(),
    category: supportRequestCategorySchema,
    status: adminSupportRequestStatusSchema,
    /** Đoạn đầu nội dung (tiêu đề dòng). */
    subject: z.string().nullable(),
    requester: z
      .object({
        userId: z.string(),
        /** null khi người dùng đã ẩn danh. */
        fullName: z.string().nullable(),
        /** Che theo vai admin (`redactPhone`); null khi không có số / đã ẩn danh. */
        phone: z.string().nullable(),
      })
      .strict()
      // null chỉ với ticket in_app cũ không gắn user (dữ liệu trước task này) — mọi yêu cầu gửi qua app đều có người gửi.
      .nullable(),
    group: z
      .object({
        id: z.string(),
        name: z.string(),
        role: supportRequesterRoleSchema.nullable(),
      })
      .strict()
      .nullable(),
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
  })
  .strict();
export type AdminSupportRequestItem = z.infer<typeof adminSupportRequestItemSchema>;

export const adminSupportRequestCountsSchema = z
  .object({
    unresolved: z.number().int().nonnegative(),
    resolved: z.number().int().nonnegative(),
    all: z.number().int().nonnegative(),
  })
  .strict();
export type AdminSupportRequestCounts = z.infer<typeof adminSupportRequestCountsSchema>;

export const adminSupportRequestListResponseSchema = z
  .object({
    data: z.array(adminSupportRequestItemSchema),
    pageInfo: pageInfoSchema,
    counts: adminSupportRequestCountsSchema,
  })
  .strict();
export type AdminSupportRequestListResponse = z.infer<typeof adminSupportRequestListResponseSchema>;

export const adminSupportRequestDetailSchema = adminSupportRequestItemSchema
  .extend({
    /** Nội dung góp ý (message inbound đầu tiên). Rỗng khi người gửi đã ẩn danh. */
    body: z.string(),
    deviceInfo: supportRequestDeviceInfoSchema.nullable(),
    attachments: z.array(supportRequestAttachmentSchema),
    /** Email admin đã TẠO HỘ yêu cầu; null = người dùng tự gửi từ app (hoặc admin đó không còn). */
    createdByEmail: z.string().nullable(),
    /** Có khi đang ở trạng thái đã xử lý. */
    resolution: z
      .object({
        note: z.string().nullable(),
        /** Email admin đã xử lý; null khi admin đó không còn. */
        resolvedByEmail: z.string().nullable(),
        resolvedAt: z.string(),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type AdminSupportRequestDetail = z.infer<typeof adminSupportRequestDetailSchema>;

/**
 * Body "Tạo yêu cầu" của admin (tạo hộ khi người dùng báo qua điện thoại / Zalo — QĐ người dùng 23/10/2026): người dùng BẮT BUỘC; KHÔNG
 * chọn nhóm (người báo không nói là nhóm nào). Không ảnh, không thiết bị. Không email, không hiện ở «Yêu cầu của tôi».
 */
export const createAdminSupportRequestSchema = z
  .object({
    userId: z.string().uuid(),
    category: supportRequestCategorySchema,
    body: z.string().trim().min(1).max(SUPPORT_REQUEST_BODY_MAX_LENGTH),
  })
  .strict();
export type CreateAdminSupportRequestBody = z.infer<typeof createAdminSupportRequestSchema>;

/** Body "Đánh dấu đã xử lý": ghi chú TUỲ CHỌN (QĐ 23/10). Chuỗi rỗng sau trim ⇒ coi như không có. */
export const resolveSupportRequestSchema = z
  .object({
    note: z.string().trim().max(SUPPORT_REQUEST_RESOLUTION_NOTE_MAX_LENGTH).optional(),
  })
  .strict();
export type ResolveSupportRequestBody = z.infer<typeof resolveSupportRequestSchema>;
